import { motion, useReducedMotion } from "motion/react";
import { ArrowDownRight, MapTrifold } from "@phosphor-icons/react";
import { AutterWordmarkOnDark, CaptainPatchImage } from "../brand/AutterBrand";

const easeOut = [0.16, 1, 0.3, 1] as const;

export function AutterStoryBand() {
  const reduce = useReducedMotion();

  return (
    <section id="story" className="relative overflow-hidden bg-[#18264b] py-24 text-foam md:py-32">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_20%_20%,#35f1c6_0,transparent_24%),linear-gradient(rgba(255,255,255,.07)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.07)_1px,transparent_1px)] [background-size:auto,64px_64px,64px_64px]"
      />
      <div className="relative mx-auto grid w-full max-w-[1400px] items-center gap-14 px-5 md:px-10 lg:grid-cols-[0.86fr_1.14fr]">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.7, ease: easeOut }}
          className="relative z-10"
        >
          <div className="inline-flex items-center gap-3 rounded-full border border-foam/20 bg-foam/8 px-4 py-2">
            <AutterWordmarkOnDark className="h-7 w-auto" />
            <span className="h-4 w-px bg-foam/25" aria-hidden />
            <span className="font-mono text-[10px] tracking-[0.14em] text-foam/70 uppercase">hosts of Same Boat</span>
          </div>
          <p className="mt-10 flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] text-[#35f1c6] uppercase">
            <MapTrifold className="size-4" weight="bold" aria-hidden />
            The story behind the harbour
          </p>
          <h2 className="display-type mt-5 max-w-[13ch] text-[clamp(2.8rem,6vw,6rem)] text-foam">
            Built by people still in the boat.
          </h2>
          <p className="mt-7 max-w-[49ch] text-lg leading-relaxed text-foam/75">
            Same Boat is the room Autter's founders wished they had while building: a place where people compare notes, ask for practical help and leave with a clearer next move.
          </p>
          <a
            href="#why"
            className="mt-9 inline-flex min-h-12 items-center gap-2 rounded-full border border-foam/35 px-5 text-sm font-semibold text-foam transition-colors hover:border-[#35f1c6] hover:bg-[#35f1c6] hover:text-[#18264b]"
          >
            Read the keeper's letter
            <ArrowDownRight className="size-4" weight="bold" aria-hidden />
          </a>
        </motion.div>
        <motion.figure
          initial={reduce ? false : { opacity: 0, x: 44, rotate: 1.8 }}
          whileInView={{ opacity: 1, x: 0, rotate: -0.7 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.85, delay: 0.08, ease: easeOut }}
          className="relative"
        >
          <div className="overflow-hidden rounded-[1.1rem] border border-foam/25 bg-[#0d1730] p-2 shadow-[0_30px_80px_rgba(4,9,22,.45)]">
            <div className="relative aspect-video overflow-hidden rounded-[0.8rem]">
              <motion.img
                src="/autter-assets/autter-hero-poster.jpg"
                alt="Autter's pixel-art seaside scene with a laptop on a picnic blanket beside the ocean."
                className="h-full w-full object-cover"
                animate={reduce ? undefined : { scale: [1.01, 1.045, 1.01] }}
                transition={reduce ? undefined : { duration: 16, repeat: Infinity, ease: "easeInOut" }}
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_54%,rgba(8,16,39,.78))]" />
              <figcaption className="absolute right-5 bottom-5 left-5 flex items-end justify-between gap-5">
                <p className="max-w-[32ch] text-sm leading-snug font-medium text-foam md:text-base">
                  Autter builds for the work. Same Boat makes room for the people doing it.
                </p>
                <span className="hidden font-mono text-[9px] tracking-widest text-[#35f1c6] uppercase sm:block">shore leave / 01</span>
              </figcaption>
            </div>
          </div>
          <motion.div
            aria-hidden
            animate={reduce ? undefined : { y: [0, -8, 0], rotate: [2, -1, 2] }}
            transition={reduce ? undefined : { duration: 5.8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -right-2 -bottom-12 rounded-full bg-[#35f1c6] p-2 shadow-2xl md:-right-8"
          >
            <CaptainPatchImage pose="lookout" className="h-28 w-28 md:h-36 md:w-36" />
          </motion.div>
        </motion.figure>
      </div>
    </section>
  );
}
